// src/hooks/useWishlist.ts
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';

interface WishlistItemWithDetails {
    id: string;
    variant_id: number;
    product_id: number;
    product_name: string;
    product_image: string;
    variant_name: string;
    price: number;
    quantity: number;
    instance_id: string;
}

export const useWishlist = () => {
    const [wishlistItems, setWishlistItems] = useState<WishlistItemWithDetails[]>([]);
    const [loading, setLoading] = useState(true);
    const [wishlistIds, setWishlistIds] = useState<Set<number>>(new Set());
    const [customerId, setCustomerId] = useState<string | null>(null);

    // Получаем customer_id из таблицы customers по auth_user_id
    const getCustomerId = useCallback(async (): Promise<string | null> => {
        // Если уже есть, возвращаем
        if (customerId) return customerId;

        // Получаем сессию
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
            console.log('No session found');
            return null;
        }

        const authUserId = session.user.id;
        console.log('Auth user ID:', authUserId);

        // Ищем customer в таблице customers
        const { data: customer, error } = await supabase
            .from('customers')
            .select('id')
            .eq('auth_user_id', authUserId)
            .single();

        if (error) {
            console.error('Error fetching customer:', error);
            return null;
        }

        console.log('Found customer ID:', customer.id);
        setCustomerId(customer.id);
        return customer.id;
    }, [customerId]);

    const fetchWishlist = useCallback(async () => {
        setLoading(true);
        try {
            const cId = await getCustomerId();
            if (!cId) {
                console.log('No customer ID, clearing wishlist');
                setWishlistItems([]);
                setWishlistIds(new Set());
                setLoading(false);
                return;
            }

            console.log('Fetching wishlist for customer:', cId);

            const { data, error } = await supabase
                .from('customer_wishlist')
                .select(`
                    id,
                    variant_id,
                    created_at,
                    variant:product_variants!inner(
                        id,
                        instance_id,
                        color,
                        material,
                        attributes,
                        quantity,
                        price,
                        selling_price,
                        product:products(
                            id,
                            name,
                            images,
                            product_code,
                            description
                        )
                    )
                `)
                .eq('customer_id', cId)
                .order('created_at', { ascending: false });

            if (error) {
                console.error('Fetch error:', error);
                throw error;
            }

            console.log('Raw wishlist data:', data);

            const formattedItems: WishlistItemWithDetails[] = (data || []).map((item: any) => {
                const variant = item.variant;
                const product = variant?.product;
                const firstImage = product?.images?.[0] || '';

                let variantName = '';
                if (variant?.color) variantName = variant.color;
                else if (variant?.material) variantName = variant.material;
                else if (variant?.attributes) {
                    const attrs = variant.attributes;
                    if (Array.isArray(attrs) && attrs.length > 0) {
                        const values = attrs.map((a: any) => a.value).filter(Boolean);
                        variantName = values.join(', ');
                    }
                }

                return {
                    id: item.id,
                    variant_id: item.variant_id,
                    product_id: product?.id,
                    product_name: product?.name || 'Товар',
                    product_image: firstImage,
                    variant_name: variantName || variant?.instance_id || 'Стандарт',
                    price: variant?.selling_price || variant?.price || 0,
                    quantity: variant?.quantity || 0,
                    instance_id: variant?.instance_id || ''
                };
            });

            setWishlistItems(formattedItems);
            const idsArray = formattedItems.map(item => item.variant_id);
            const idsSet = new Set(idsArray);
            setWishlistIds(idsSet);

            console.log('Formatted items count:', formattedItems.length);
            console.log('Wishlist IDs:', Array.from(idsSet));
        } catch (error) {
            console.error('Error fetching wishlist:', error);
            toast.error('Ошибка загрузки избранного');
        } finally {
            setLoading(false);
        }
    }, [getCustomerId]);

    // src/hooks/useWishlist.ts - Временная диагностическая версия

    const addToWishlist = useCallback(async (variantId: number) => {
        try {
            console.log('=== STEP 1: addToWishlist called ===');
            console.log('VariantId:', variantId);

            // STEP 2: Проверяем сессию
            console.log('STEP 2: Getting session...');
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();
            console.log('Session result:', {
                hasSession: !!session,
                userId: session?.user?.id,
                error: sessionError?.message
            });

            if (!session?.user) {
                toast.error('Войдите в аккаунт');
                return false;
            }

            const authUserId = session.user.id;
            console.log('STEP 3: Auth user ID:', authUserId);

            // STEP 4: Ищем customer
            console.log('STEP 4: Looking for customer in customers table...');
            const { data: customer, error: customerError } = await supabase
                .from('customers')
                .select('id, auth_user_id')
                .eq('auth_user_id', authUserId)
                .maybeSingle();

            console.log('Customer query result:', {
                customer,
                error: customerError?.message,
                errorCode: customerError?.code
            });

            if (customerError) {
                console.error('Customer fetch error:', customerError);
                toast.error(`Ошибка: ${customerError.message}`);
                return false;
            }

            if (!customer) {
                console.log('STEP 5: Customer not found, creating...');
                // Создаем customer
                const { data: newCustomer, error: createError } = await supabase
                    .from('customers')
                    .insert({
                        auth_user_id: authUserId,
                        email: session.user.email,
                        is_active: true,
                        email_confirmed: true,
                        created_at: new Date().toISOString()
                    })
                    .select()
                    .single();

                console.log('Create customer result:', { newCustomer, error: createError?.message });

                if (createError) {
                    toast.error(`Ошибка создания пользователя: ${createError.message}`);
                    return false;
                }

                setCustomerId(newCustomer.id);
                var cId = newCustomer.id;
            } else {
                console.log('STEP 5: Customer found, ID:', customer.id);
                setCustomerId(customer.id);
                var cId = customer.id;
            }

            // STEP 6: Проверяем, есть ли уже в избранном
            console.log('STEP 6: Checking if already in wishlist...');
            const { data: existing, error: checkError } = await supabase
                .from('customer_wishlist')
                .select('id')
                .eq('customer_id', cId)
                .eq('variant_id', variantId)
                .maybeSingle();

            console.log('Check existing result:', { existing, error: checkError?.message });

            if (existing) {
                console.log('STEP 7: Already in wishlist, updating state only');
                const newIds = new Set(wishlistIds);
                newIds.add(variantId);
                setWishlistIds(newIds);
                return true;
            }

            // STEP 7: Добавляем
            console.log('STEP 7: Inserting into wishlist...');
            const { data: insertData, error: insertError } = await supabase
                .from('customer_wishlist')
                .insert({
                    customer_id: cId,
                    variant_id: variantId,
                    created_at: new Date().toISOString()
                })
                .select();

            console.log('Insert result:', {
                insertData,
                error: insertError?.message,
                errorCode: insertError?.code,
                errorDetails: insertError?.details
            });

            if (insertError) {
                console.error('Insert error:', insertError);
                toast.error(`Ошибка вставки: ${insertError.message}`);
                return false;
            }

            console.log('STEP 8: Update local state');
            const newIds = new Set(wishlistIds);
            newIds.add(variantId);
            setWishlistIds(newIds);

            console.log('STEP 9: Refreshing wishlist');
            await fetchWishlist();

            toast.success('Добавлено в избранное');
            return true;
        } catch (error: any) {
            console.error('=== CATCH ERROR ===');
            console.error('Error message:', error.message);
            console.error('Error code:', error.code);
            console.error('Error details:', error.details);
            console.error('Full error:', error);
            toast.error(`Ошибка: ${error.message || 'Ошибка добавления в избранное'}`);
            return false;
        }
    }, [fetchWishlist, wishlistIds]);

    const removeFromWishlist = useCallback(async (variantId: number) => {
        try {
            console.log('=== REMOVE FROM WISHLIST ===');
            console.log('VariantId:', variantId);

            const cId = await getCustomerId();
            if (!cId) {
                toast.error('Войдите в аккаунт');
                return false;
            }

            const { error } = await supabase
                .from('customer_wishlist')
                .delete()
                .eq('customer_id', cId)
                .eq('variant_id', variantId);

            if (error) throw error;

            // Обновляем состояние
            const newIds = new Set(wishlistIds);
            newIds.delete(variantId);
            setWishlistIds(newIds);

            setWishlistItems(prev => prev.filter(item => item.variant_id !== variantId));

            toast.success('Удалено из избранного');
            return true;
        } catch (error) {
            console.error('Error removing from wishlist:', error);
            toast.error('Ошибка удаления из избранного');
            return false;
        }
    }, [getCustomerId, wishlistIds]);

    const isInWishlist = useCallback((variantId: number) => {
        return wishlistIds.has(variantId);
    }, [wishlistIds]);

    // Инициализация
    useEffect(() => {
        const init = async () => {
            await getCustomerId();
            await fetchWishlist();
        };
        init();
    }, []);

    return {
        wishlistItems,
        loading,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        refreshWishlist: fetchWishlist
    };
};